/**
 * Browser auth-session management.
 *
 * Only the short-lived access token is visible to JavaScript. The rotating
 * refresh credential lives in an HttpOnly SameSite cookie and is never read,
 * returned, or persisted by this module.
 */

import { currentBrowserProductSurface } from './productSurface';
import { purgePrivateChatStorage } from './privateChatStorage';

export interface AuthSession {
  accessToken: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const ACCESS_TOKEN_KEY = 'boss_token';
const USER_KEY = 'boss_user';
const LEGACY_ACCESS_TOKEN_KEY = 'vasari_token';
const LEGACY_USER_KEY = 'vasari_user';
const LEGACY_SESSION_KEY = 'boss_auth_session';
const LEGACY_REFRESH_TOKEN_KEY = 'boss_refresh_token';

function browserStorage(): StorageLike | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function validSession(value: unknown): value is AuthSession {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<AuthSession>;
  return typeof candidate.accessToken === 'string'
    && candidate.accessToken.length > 0;
}

export function readAccessToken(storage: StorageLike | null = browserStorage()): string | null {
  if (!storage) return null;
  try {
    const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
    return accessToken && accessToken.length > 0 ? accessToken : null;
  } catch {
    return null;
  }
}

export function storeAccessToken(
  session: AuthSession,
  storage: StorageLike | null = browserStorage(),
): void {
  if (!validSession(session)) throw new Error('A non-empty access token is required');
  if (!storage) return;
  storage.setItem(ACCESS_TOKEN_KEY, session.accessToken);
  // Purge credentials written by pre-cookie builds as soon as a new access
  // token is accepted.
  storage.removeItem(LEGACY_SESSION_KEY);
  storage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
}

export function purgeLegacyRefreshCredentials(
  storage: StorageLike | null = browserStorage(),
): void {
  if (!storage) return;
  storage.removeItem(LEGACY_SESSION_KEY);
  storage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
}

export function clearAuthSession(storage: StorageLike | null = browserStorage()): void {
  if (!storage) return;
  purgePrivateChatStorage(storage);
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(LEGACY_SESSION_KEY);
  storage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  storage.removeItem(USER_KEY);
  storage.removeItem(LEGACY_ACCESS_TOKEN_KEY);
  storage.removeItem(LEGACY_USER_KEY);
}

function productSurfaceHeader(storage: StorageLike | null): Record<string, string> {
  try {
    const surface = currentBrowserProductSurface(storage);
    return surface === 'bos' || surface === 'hos'
      ? { 'X-BOSS-Product-Surface': surface }
      : {};
  } catch {
    return {};
  }
}

function currentOrigin(): string {
  try {
    return globalThis.location?.origin || 'http://localhost';
  } catch {
    return 'http://localhost';
  }
}

function normalizeInput(input: RequestInfo | URL, origin: string): RequestInfo | URL {
  if (typeof input === 'string' && input.startsWith('api/')) return `${origin}/${input}`;
  if (typeof input === 'string' && input.startsWith('/')) return `${origin}${input}`;
  return input;
}

function apiPath(input: RequestInfo | URL, origin: string): string | null {
  try {
    const raw = input instanceof Request ? input.url : input.toString();
    const normalized = raw.startsWith('api/') ? `/${raw}` : raw;
    const url = new URL(normalized, `${origin}/`);
    return url.origin === origin && url.pathname.startsWith('/api/') ? url.pathname : null;
  } catch {
    return null;
  }
}

function requestWithAuth(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  origin: string,
  accessToken: string | null,
  storage: StorageLike | null,
): Request {
  const normalized = normalizeInput(input, origin);
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  for (const [key, value] of Object.entries(productSurfaceHeader(storage))) {
    if (!headers.has(key)) headers.set(key, value);
  }
  return new Request(normalized, { ...init, headers });
}

function notifySessionExpired(): void {
  try {
    globalThis.dispatchEvent?.(new Event('boss:session-expired'));
    // Compatibility with the existing Dashboard auth-health banner.
    globalThis.dispatchEvent?.(new Event('boss-auth-expired'));
  } catch {
    // Event support is optional (SSR and unit tests).
  }
}

const NO_REFRESH_PATHS = new Set([
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
]);

interface AuthenticatedFetchOptions {
  storage?: StorageLike | null;
  origin?: string;
  refreshLock?: RefreshLock;
}

type RefreshLock = <T>(task: () => Promise<T>) => Promise<T>;

export interface AuthenticatedFetchClient {
  fetch: FetchLike;
  logout: () => Promise<boolean>;
}

/**
 * Creates a fetch client with one refresh operation shared by every request.
 * A request that receives its 401 after another request has already rotated
 * the pair retries with the new access token without rotating a second time.
 */
export function createAuthenticatedFetch(
  fetchImpl: FetchLike,
  options: AuthenticatedFetchOptions = {},
): AuthenticatedFetchClient {
  const storage = options.storage === undefined ? browserStorage() : options.storage;
  const origin = options.origin ?? currentOrigin();
  const browserLocks = globalThis.navigator?.locks;
  const crossContextLock: RefreshLock | null = options.refreshLock
    ?? (browserLocks
      ? async <T>(task: () => Promise<T>) => browserLocks.request(
          'boss-auth-refresh',
          { mode: 'exclusive' },
          task,
        )
      : null);
  let refreshInFlight: Promise<AuthSession> | null = null;

  async function rotateTokens(): Promise<AuthSession> {
    const beforeAccessToken = readAccessToken(storage);
    if (!beforeAccessToken) throw new Error('No access token is available');
    const response = await fetchImpl(`${origin}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        ...productSurfaceHeader(storage),
      },
      credentials: 'same-origin',
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok || !validSession(body)) {
      const currentAccessToken = readAccessToken(storage);
      if (currentAccessToken && currentAccessToken !== beforeAccessToken) {
        return { accessToken: currentAccessToken };
      }

      if ([400, 401, 403].includes(response.status)
        && currentAccessToken === beforeAccessToken) {
        clearAuthSession(storage);
        notifySessionExpired();
      }
      throw new Error('Unable to refresh the authentication session');
    }
    storeAccessToken(body, storage);
    return body;
  }

  function sharedRefresh(sentAccessToken: string): Promise<AuthSession> {
    if (!crossContextLock) {
      // localStorage cannot provide an atomic cross-tab mutex. Failing closed
      // is safer than issuing two rotations and potentially resurrecting a
      // session after logout on a browser without Web Locks support.
      clearAuthSession(storage);
      notifySessionExpired();
      return Promise.reject(new Error('This browser cannot safely refresh the authentication session'));
    }
    if (!refreshInFlight) {
      refreshInFlight = crossContextLock(async () => {
        // The Web Lock is shared by tabs and workers. Re-check after acquiring
        // it because another browser context may have completed rotation while
        // this context was waiting.
        const currentAccessToken = readAccessToken(storage);
        if (!currentAccessToken) throw new Error('No access token is available');
        if (currentAccessToken !== sentAccessToken) return { accessToken: currentAccessToken };
        return rotateTokens();
      }).finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  }

  async function refreshAfter401(sentAccessToken: string): Promise<AuthSession> {
    const currentAccessToken = readAccessToken(storage);
    if (!currentAccessToken) throw new Error('No access token is available');

    // A faster request (or another tab) already rotated the session while
    // this request was waiting for its 401 response.
    if (currentAccessToken !== sentAccessToken) return { accessToken: currentAccessToken };
    return sharedRefresh(sentAccessToken);
  }

  const authenticatedFetch: FetchLike = async (input, init) => {
    const path = apiPath(input, origin);
    if (!path || NO_REFRESH_PATHS.has(path)) {
      return fetchImpl(normalizeInput(input, origin), init);
    }

    const initialAccessToken = readAccessToken(storage);
    const request = requestWithAuth(input, init, origin, initialAccessToken, storage);
    const response = await fetchImpl(request.clone());
    if (response.status !== 401) return response;
    if (!initialAccessToken) {
      notifySessionExpired();
      return response;
    }

    const refreshed = await refreshAfter401(initialAccessToken);
    const retry = requestWithAuth(request, undefined, origin, refreshed.accessToken, storage);
    return fetchImpl(retry);
  };

  async function logout(): Promise<boolean> {
    let clearedInsideLock = false;
    try {
      // Finish an in-flight same-context rotation before entering the
      // cross-context lock. Other tabs use this same named Web Lock.
      if (refreshInFlight) await refreshInFlight.catch(() => null);

      const logoutLock: RefreshLock = crossContextLock ?? (async <T>(task: () => Promise<T>) => task());
      return await logoutLock(async () => {
        try {
          const sendLogout = () => fetchImpl(`${origin}/api/auth/logout`, {
            method: 'POST',
            headers: {
              ...productSurfaceHeader(storage),
            },
            credentials: 'same-origin',
          });
          const response = await sendLogout();
          return response.ok;
        } catch {
          // Signing out locally must still succeed if the server is unavailable.
          return false;
        } finally {
          clearAuthSession(storage);
          clearedInsideLock = true;
        }
      });
    } finally {
      // Covers an unexpected lock-manager failure before the callback runs.
      if (!clearedInsideLock) {
        clearAuthSession(storage);
      }
    }
  }

  return { fetch: authenticatedFetch, logout };
}

const nativeFetch = globalThis.fetch.bind(globalThis) as FetchLike;
const browserClient = createAuthenticatedFetch(nativeFetch);

export const authenticatedFetch: FetchLike = browserClient.fetch;
export const logoutAuthSession = browserClient.logout;

/** Install once at application startup so legacy direct fetch calls are safe. */
let installed = false;
export function installAuthenticatedFetch(): void {
  if (installed) return;
  installed = true;
  purgeLegacyRefreshCredentials();
  globalThis.fetch = authenticatedFetch as typeof globalThis.fetch;
  globalThis.addEventListener?.('storage', (event: Event) => {
    const storageEvent = event as StorageEvent;
    if (storageEvent.key === ACCESS_TOKEN_KEY
      && storageEvent.oldValue
      && storageEvent.newValue === null) {
      notifySessionExpired();
    }
  });
}
